import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { FaInstagram } from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({
    user_fname: '',
    user_lname: '',
    user_email: '',
    user_phone: '',
    service_type: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [timeoutId, setTimeoutId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Handlers for independent field updates
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.user_fname.trim()) newErrors.user_fname = 'First name is required';
    if (!formData.user_lname.trim()) newErrors.user_lname = 'Last name is required';
    if (!formData.user_email.trim()) {
      newErrors.user_email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.user_email)) {
      newErrors.user_email = 'Invalid email format';
    }
    if (!formData.user_phone.trim()) {
      newErrors.user_phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.user_phone)) {
      newErrors.user_phone = 'Enter a valid 10-digit number';
    }
    if (!formData.service_type) newErrors.service_type = 'Please select a service';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    // Start the countdown
    setIsCountingDown(true);
    setCountdown(5);
    setErrorMessage('');
    
    // Set up the timeout for sending the email
    const id = setTimeout(() => {
      sendEmail();
      setIsCountingDown(false);
      setTimeoutId(null);
    }, 5000);
    
    setTimeoutId(id);
    
    // Start countdown interval
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const sendEmail = () => {
    setIsSubmitting(true);
    
    // Mapping data for EmailJS Template
    const templateParams = {
      name: `${formData.user_fname} ${formData.user_lname}`,
      email: formData.user_email,
      phone: formData.user_phone,
      service_type: formData.service_type,
      message: formData.message,
    };
    
    // Sending email via EmailJS
    emailjs.send(
      'service_jfuvl96', 
      'template_2wtfmqg', 
      templateParams, 
      'udkUdZqPu-UkvmHfp'
    )
    .then((response) => {
      // Show Success Alert
      alert('Message sent successfully! Your inquiry has reached Samyak Decor.');
      
      // Reset Form State
      setFormData({
        user_fname: '', user_lname: '', user_email: '',
        user_phone: '', service_type: '', message: ''
      });
      setIsSubmitting(false);
    })
    .catch((error) => {
      console.error('FAILED...', error);
      setErrorMessage('Service unavailable. Please check your connection or Public Key.');
      setIsSubmitting(false);
    });
  };
  
  const handleUndo = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
      setIsCountingDown(false);
      setCountdown(5);
    }
  };

  // Staggered animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <section id="contact" className="min-h-screen relative overflow-hidden">
      {/* Background with Overlay */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center bg-no-repeat">
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center py-20">
        <div className="container mx-auto px-4">
          <motion.div 
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center"
          >
            {/* Left Side: Contact Information */}
            <motion.div variants={item} className="text-white">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                Let's Create Something <span className="text-yellow-400">Extraordinary</span>
              </h2>
              <p className="text-gray-300 text-base sm:text-lg mb-8 sm:mb-10 max-w-lg">
                Transform your space with our premium interior design expertise. Connect with us to bring your vision to life.
              </p>
              
              <div className="space-y-6">
                <motion.div 
                  variants={item}
                  className="flex items-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-yellow-400 bg-opacity-20 flex items-center justify-center mr-4 group-hover:bg-opacity-30 transition-all">
                    <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">Visit Our Studio</h3>
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Samyak+Decor+Gangadham+Pune" 
                      target="_blank" 
                      className="text-gray-300 hover:text-yellow-400 transition-colors"
                    >
                      Office No - 304 F/2 Gangadham Fase-2, Pune - 411037
                    </a>
                  </div>
                </motion.div>
                
                <motion.div 
                  variants={item}
                  className="flex items-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-yellow-400 bg-opacity-20 flex items-center justify-center mr-4 group-hover:bg-opacity-30 transition-all">
                    <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">Call Us</h3>
                    <a 
                      href="tel:+919860138383" 
                      className="text-gray-300 hover:text-yellow-400 transition-colors"
                    >
                      +91 98601 38383
                    </a>
                    <br />
                    <a 
                      href="tel:+917498694117" 
                      className="text-gray-300 hover:text-yellow-400 transition-colors"
                    >
                      +91 7498 694 117
                    </a>
                  </div>
                </motion.div>
                
                <motion.div 
                  variants={item}
                  className="flex items-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-yellow-400 bg-opacity-20 flex items-center justify-center mr-4 group-hover:bg-opacity-30 transition-all">
                    <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">Email Us</h3>
                    <a 
                      href="mailto:Samyakdecor@gmail.com" 
                      className="text-gray-300 hover:text-yellow-400 transition-colors"
                    >
                      Samyakdecor@gmail.com
                    </a>
                  </div>
                </motion.div>
                
                <motion.div 
                  variants={item}
                  className="flex items-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-yellow-400 bg-opacity-20 flex items-center justify-center mr-4 group-hover:bg-opacity-30 transition-all">
                    <FaInstagram className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">Instagram</h3>
                    <a 
                      href="https://www.instagram.com/samyakdecor?igsh=MXNiY280dno4ZXR1Nw==" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-yellow-400 transition-colors"
                    >
                      @samyakdecor
                    </a>
                  </div>
                </motion.div>
              </div>
            </motion.div>
            
            {/* Right Side: Dark Theme Form */}
            <motion.div 
              variants={item}
              className="bg-gray-900 rounded-2xl p-8 shadow-2xl border border-gray-700"
            >
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-8">Start Your Project</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm sm:text-base mb-2 font-medium">First Name</label>
                    <input
                      type="text" 
                      name="user_fname" 
                      value={formData.user_fname}
                      onChange={handleChange}
                      className="w-full bg-gray-800 border border-gray-700 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all placeholder-gray-500"
                      placeholder="John"
                    />
                    {errors.user_fname && <p className="text-red-400 text-sm mt-1">{errors.user_fname}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 text-sm sm:text-base mb-2 font-medium">Last Name</label>
                    <input
                      type="text" 
                      name="user_lname" 
                      value={formData.user_lname}
                      onChange={handleChange}
                      className="w-full bg-gray-800 border border-gray-700 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all placeholder-gray-500"
                      placeholder="Doe"
                    />
                    {errors.user_lname && <p className="text-red-400 text-sm mt-1">{errors.user_lname}</p>}
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm sm:text-base mb-2 font-medium">Email Address</label>
                  <input
                    type="email" 
                    name="user_email" 
                    value={formData.user_email}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all placeholder-gray-500"
                    placeholder="your@email.com"
                  />
                  {errors.user_email && <p className="text-red-400 text-sm mt-1">{errors.user_email}</p>}
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm sm:text-base mb-2 font-medium">Phone Number</label>
                  <input
                    type="tel" 
                    name="user_phone" 
                    value={formData.user_phone}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all placeholder-gray-500"
                    placeholder="+91 98601 38383"
                  />
                  {errors.user_phone && <p className="text-red-400 text-sm mt-1">{errors.user_phone}</p>}
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm sm:text-base mb-2 font-medium">Service Required</label>
                  <select 
                    name="service_type" 
                    value={formData.service_type}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all appearance-none"
                  >
                    <option value="" className="bg-gray-800">Select a service</option>
                    <option value="turnkey" className="bg-gray-800">Turnkey Solutions</option>
                    <option value="residential" className="bg-gray-800">Residential Interiors</option>
                    <option value="commercial" className="bg-gray-800">Commercial & Offices</option>
                    <option value="builder" className="bg-gray-800">Builder Common Areas</option>
                  </select>
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {errors.service_type && <p className="text-red-400 text-sm mt-1">{errors.service_type}</p>}
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm sm:text-base mb-2 font-medium">Project Details</label>
                  <textarea
                    name="message" 
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full bg-gray-800 border border-gray-700 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all placeholder-gray-500 resize-none"
                    placeholder="Tell us about your project requirements..."
                  ></textarea>
                  {errors.message && <p className="text-red-400 text-sm mt-1">{errors.message}</p>}
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || isCountingDown}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-3 sm:py-4 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </div>
                    ) : isCountingDown ? (
                      <div className="text-center">
                        <div className="font-bold">Sending in {countdown}s</div>
                        <div className="text-sm">Click below to cancel</div>
                      </div>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                  
                  {isCountingDown && (
                    <button
                      type="button"
                      onClick={handleUndo}
                      className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 sm:py-3 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      Cancel Submission
                    </button>
                  )}
                </div>
                
                {errorMessage && (
                  <div className="text-center p-3 bg-red-900 bg-opacity-50 rounded-lg border border-red-700">
                    <p className="text-red-300 text-sm">{errorMessage}</p>
                  </div>
                )}
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;